import React from 'react';
import classnames from 'classnames';
import { ButtonData_Type, Link_Target, type RichContent } from '@wix/ricos';
import loadable from '@wix/yoshi-flow-editor/loadable';
import { PaywallBanner } from '@wix/communities-blog-client-common/dist/esm/components';
import { useSelector } from '@app/external/common/components/runtime-context';
import useFontClassName from '@app/external/common/hooks/use-font-class-name';
import { getAppSettings } from '@app/external/common/selectors/app-settings-base-selectors';
import { getSection } from '@app/external/common/selectors/section-selectors';
import { getTopology } from '@app/external/common/store/topology/topology-selectors';
import type { NormalizedPost } from '@app/external/common/types';
import styles from './post-content.scss';

type PaywallBannerWithLinkProps = {
  paywallContent: RichContent;
};

const RichContentViewer = loadable(
  () =>
    import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "rich-content-viewer" */ '../../../rich-content/components/rich-content-viewer'
    ),
  {
    fallback: null,
  },
);

const PaywallBannerWithLink: React.FC<PaywallBannerWithLinkProps> = ({ paywallContent }) => {
  const appSettings = useSelector(getAppSettings);
  const section = useSelector(getSection);
  const pricingPlansUrl = useSelector((state) => state.pricingPlansUrl);
  const { baseUrl } = useSelector(getTopology);
  const subscribeURL = React.useMemo(() => {
    try {
      if (baseUrl && typeof pricingPlansUrl === 'string') {
        return new URL(baseUrl + pricingPlansUrl).href;
      }
    } catch {
      return undefined;
    }
  }, [baseUrl, pricingPlansUrl]);

  const modifiedPaywallContent = React.useMemo(() => {
    try {
      if ('nodes' in paywallContent) {
        const buttonNode = paywallContent.nodes[2];

        if (buttonNode.buttonData && !buttonNode.buttonData?.link) {
          buttonNode.buttonData.type = ButtonData_Type.LINK;
          const link = {
            url: subscribeURL,
            anchor: undefined,
            target: Link_Target.SELF,
            rel: {
              nofollow: true,
              sponsored: false,
            },
          };

          buttonNode.buttonData.link = link;
        }

        return paywallContent;
      }
    } catch {
      return paywallContent;
    }
  }, [paywallContent, subscribeURL]);

  return (
    <PaywallBanner
      banner={modifiedPaywallContent}
      viewer={RichContentViewer}
      appSettings={appSettings}
      section={section}
      className={styles.banner}
    />
  );
};

type PostContentProps = {
  post: NormalizedPost;
};

export const PostContent: React.FC<PostContentProps> = ({ post }) => {
  const { contentFontClassName } = useFontClassName();

  const paywallContent = post?.paywallRichContent as RichContent;

  const postContentBodyClass = 'post-content__body';

  return (
    <article className={contentFontClassName}>
      <div
        className={classnames(postContentBodyClass, styles.rceNextView)}
        suppressHydrationWarning
      >
        <RichContentViewer
          initialRawState={post.content}
          post={post}
          parentClass={postContentBodyClass}
        />
      </div>
      {post?.canSeePaidContent === false && paywallContent && (
        <div className={styles.paywallContainer}>
          <PaywallBannerWithLink paywallContent={paywallContent} />
        </div>
      )}
    </article>
  );
};

export default PostContent;
